import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import { v4 as uuid } from 'uuid';
import jsQR from 'jsqr';
import { toast } from 'react-hot-toast';
import { t } from 'i18next';
import api from '@/services/api';
import { uploadFileFromPresignedUrl } from '@/utils/aws-sdk';
import { DashboardContent } from '@/layouts/dashboard';
import { useQuestionBook } from '@/hooks/useQuestionBook';

// Rotulo (pt-BR) e cor do badge por status do item na lista de resultados.
// 'processing' = ja enviado, aguardando a correcao (OCR) rodar - nao e mais
// chamado "ok" pra deixar claro que a nota ainda nao saiu. 'online' = aluno ja
// tinha feito a prova pelo app (sem answer-sheet), entao nao ha nada a enviar.
const STATUS_META = {
  pending: { label: 'Enviando', badge: 'primary' },
  processing: { label: 'Processando', badge: 'warning' },
  online: { label: 'Leitura concluída', badge: 'success' },
  error: { label: 'Erro no envio', badge: 'danger' },
  skipped: { label: 'Ignorado', badge: 'warning' }
};

// Upload em lote de cartoes-resposta escaneados (Opcao A, fase 3). Le o QR de
// cada imagem (jsQR), resolve o token -> aluno (backend), e envia reusando o
// mesmo fluxo do StudentCard (presign S3 -> upload -> cria answer_sheet).
// QR ilegivel / erro nao trava o lote: cada arquivo tem seu resultado.
export function BulkAnswerSheetUpload() {
  const { id, selectedTab } = useParams();
  const navigate = useNavigate();
  const client = useClient();
  const { getQuestionBook, questionBook } = useQuestionBook();
  const [results, setResults] = useState([]); // { fileName, status, message, studentName }
  const [processing, setProcessing] = useState(false);
  // user-id (number) -> question-books-user (ou null se o aluno ainda nem comecou a prova)
  const [studentsById, setStudentsById] = useState({});

  useEffect(() => {
    getQuestionBook(parseInt(id));
    client.fetch(`/users/question_books/${id}/able_to_answer_sheet`).then(({ data }) => {
      const map = {};
      (data || []).forEach(student => {
        map[student.id] = student['question-books-user'] ?? null;
      });
      setStudentsById(map);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Desenha uma regiao (sx,sy,sw,sh) da imagem num canvas reduzido a no maximo
  // maxDim px, com suavizacao ligada. O downscale suavizado (nao nearest-neighbor)
  // e o que de fato limpa o ruido de textura do papel/impressao que atrapalha o
  // jsQR - testado empiricamente com fotos reais de celular (ver PROVA_EM_LOTE):
  // a imagem em resolucao nativa falhou em 100% dos casos reais.
  const drawScaledRegion = (img, sx, sy, sw, sh, maxDim) => {
    const scale = Math.min(1, maxDim / Math.max(sw, sh));
    const dw = Math.max(1, Math.round(sw * scale));
    const dh = Math.max(1, Math.round(sh * scale));
    const canvas = document.createElement('canvas');
    canvas.width = dw;
    canvas.height = dh;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, dw, dh);
    return ctx.getImageData(0, 0, dw, dh);
  };

  const decodeFromImageData = imageData => jsQR(imageData.data, imageData.width, imageData.height)?.data || null;

  // decodifica o QR de um arquivo de imagem via canvas + jsQR. O QR e impresso
  // pequeno no canto superior direito do cartao-resposta (ver PrintedExam), entao
  // tentamos primeiro so essa regiao com downscale suavizado (mais rapido e mais
  // confiavel), caindo para a imagem inteira (e por fim resolucao nativa) se
  // necessario.
  const decodeQrFromImage = file =>
    new Promise(resolve => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        try {
          const w = img.naturalWidth;
          const h = img.naturalHeight;

          const cropX = Math.floor(w * 0.5);
          const cropW = w - cropX;
          const cropH = Math.floor(h * 0.3);

          let value = decodeFromImageData(drawScaledRegion(img, cropX, 0, cropW, cropH, 900));
          if (!value) {
            value = decodeFromImageData(drawScaledRegion(img, 0, 0, w, h, 1600));
          }
          if (!value) {
            value = decodeFromImageData(drawScaledRegion(img, 0, 0, w, h, Math.max(w, h)));
          }

          if (!value) {
            console.warn('[BulkAnswerSheetUpload] QR nao decodificado', { file: file.name, width: w, height: h });
          }
          resolve(value);
        } catch (e) {
          console.warn('[BulkAnswerSheetUpload] erro ao decodificar QR', { file: file.name, error: e?.message });
          resolve(null);
        } finally {
          URL.revokeObjectURL(url);
        }
      };
      img.onerror = () => {
        console.warn('[BulkAnswerSheetUpload] falha ao carregar imagem', { file: file.name });
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });

  const resolveStudent = async token => {
    // { user-id, question-book-id, name } ou lanca em token invalido (422)
    const res = await api.get(`answer_sheets/resolve?token=${encodeURIComponent(token)}`);
    return res?.data;
  };

  const uploadForStudent = async (file, userId) => {
    const ext = file.name.includes('.') ? '.' + file.name.split('.').pop() : '';
    const renamed = new File([file], uuid().replaceAll('-', '') + ext, { type: file.type });

    const { data, error } = await client.mutate(`question_books/${id}/answer_sheets/presign_s3_url`, {
      file_key: `uploads/answer_sheet/${renamed.name}`,
      content_type: renamed.type
    });
    if (error) throw new Error('presign');

    const uploadedUrl = await uploadFileFromPresignedUrl(data['presigned-url'], renamed);
    if (!uploadedUrl) throw new Error('upload');

    const { error: createError } = await client.mutate(`question_books/${id}/answer_sheets`, {
      url: uploadedUrl,
      'question-books-user-attributes': { 'user-id': userId, 'question-book-id': id }
    });
    if (createError) throw new Error('create');
  };

  const handleFiles = async e => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setProcessing(true);
    setResults(files.map(f => ({ fileName: f.name, status: 'pending', message: t('common.processing', 'Processando...') })));

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const update = patch => setResults(prev => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
      try {
        const isImage = (file.type || '').startsWith('image/');
        if (!isImage) {
          update({ status: 'skipped', message: 'PDF: leia o QR não é suportado no lote — envie por aluno' });
          continue;
        }
        const qr = await decodeQrFromImage(file);
        if (!qr) {
          update({ status: 'error', message: 'QR ilegível — reenvie com mais nitidez ou por aluno' });
          continue;
        }

        const student = await resolveStudent(qr);
        update({ studentName: student?.name });

        // O QR imprime o question-book em que o cartao foi gerado - se o
        // professor escaneou o lote na tela de OUTRA prova, o token e valido
        // mas pertence a outro caderno. Sem essa checagem daria pra criar um
        // answer_sheet na prova errada (foi assim que o bug original apareceu).
        if (String(student?.['question-book-id']) !== String(id)) {
          update({ status: 'error', message: 'Cartão de resposta não corresponde a prova' });
          continue;
        }

        // Sem answer-sheet mas com question-books-user ja existente so acontece
        // quando o aluno respondeu pelo app (nao ha outro jeito de existir esse
        // registro sem um envio offline previo) - ver StudentCard.jsx.
        const qbu = studentsById[student['user-id']];
        if (qbu && !qbu['answer-sheet']) {
          update({ status: 'online', message: `${student?.name} — prova já realizada online` });
          continue;
        }

        update({ message: `Enviando para ${student?.name}...` });
        await uploadForStudent(file, student['user-id']);
        update({ status: 'processing', message: `Enviado — processando correção (${student?.name})` });
      } catch (err) {
        console.warn('[BulkAnswerSheetUpload] falha no envio', { file: file.name, status: err?.response?.status, error: err?.message });
        const status = err?.response?.status;
        const apiError = err?.response?.data?.error;
        let msg;
        if (status === 401) {
          msg = 'Sessão expirada — faça login novamente';
        } else if (status === 404) {
          msg = 'Este aluno não está mais cadastrado na plataforma';
        } else if (status === 422 && typeof apiError === 'string') {
          msg = 'QR inválido/adulterado';
        } else {
          msg = 'Falha no envio';
        }
        update({ status: 'error', message: msg });
      }
    }

    setProcessing(false);
    toast.success(t('exams.bulkDone', 'Lote processado'));
  };

  const breadcrumbs = {
    title: t('exams.breadcrumbTitle', 'Avaliações'),
    nav: [
      {
        route: `/avaliacoes/${selectedTab}`,
        name: selectedTab === 'provas' ? 'Provas' : 'Simulados',
        isActive: false
      },
      {
        name: questionBook?.title,
        isActive: true
      }
    ]
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <h1 className="u-mb-2">{t('exams.bulkUploadTitle', 'Enviar cartões-resposta em lote')}</h1>
      {questionBook?.title && <h2 className="u-mb-2">{questionBook.title}</h2>}

      <div className="u-mb-4 text-neutral-dark">
        <p>{t('exams.bulkUploadInstructionsTitle', 'Instruções:')}</p>
        <ol style={{ listStyle: 'decimal', paddingLeft: '1.25rem' }}>
          <li>{t('exams.bulkUploadInstructionStep1', 'Selecione as imagens dos cartões escaneados (o QRCode no cartão identificará o aluno)')}</li>
          <li>{t('exams.bulkUploadInstructionStep2', 'Aguarde o processamento da leitura')}</li>
          <li>{t('exams.bulkUploadInstructionStep3', 'Clique em "Enviar para correção".')}</li>
        </ol>
      </div>

      <div
        className="u-mb-4"
        style={{ display: 'flex', alignItems: 'center', gap: 12 }}
      >
        <label className={processing ? 'btn btn--wide btn--primary disabled' : 'btn btn--wide btn--primary'}>
          {t('button.selectFiles', 'Selecione as imagens')}
          <input
            type="file"
            accept="image/*"
            multiple
            hidden
            disabled={processing}
            onChange={handleFiles}
          />
        </label>

        <button
          type="button"
          className="btn btn--wide btn--primary"
          disabled={processing}
          onClick={() => navigate(`/avaliacoes/${selectedTab}/offline/${id}`)}
        >
          {t('button.sendForCorrection', 'Enviar para correção')}
        </button>
      </div>

      {results.length > 0 && (
        <ul
          className="u-mt-4"
          style={{ listStyle: 'none', padding: 0 }}
        >
          {results.map((r, i) => (
            <li
              key={i}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #eee' }}
            >
              <span className={`badge badge--tiny badge--${STATUS_META[r.status]?.badge || 'primary'}`}>{STATUS_META[r.status]?.label || r.status}</span>
              <strong>{r.fileName}</strong>
              <span style={{ color: '#666' }}>— {r.message}</span>
            </li>
          ))}
        </ul>
      )}
    </DashboardContent>
  );
}
